import { MY_SOURCES_CHANGEBOARDING_BANNER_MODAL_ID } from '@/atoms/my-sources-changeboarding-banner-modal-id';
import { PENDING_TEAM_INVITE_MODAL_ID } from '@/atoms/pending-team-invite-modal-ids';
import { Tier } from '@/lib/modalTiers';
import {
  DEFAULT_LANDING_PAGE_BANNER_MODAL_ID,
  HEIDI_2_CONFIRM_ACCESS_MODAL_ID,
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
  HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
  HEIDI_2_NEW_USER_ONBOARDING_BANNER_MODAL_ID,
} from '@heidi/agents/contracts';
import {
  CLINICIAN_TRIAL_ENDED_MODAL_ID,
  COMPLIMENTARY_TRIAL_END_BANNER_MODAL_ID,
  COMPLIMENTARY_TRIAL_ENDED_MODAL_ID,
  COMPLIMENTARY_USAGE_BANNER_MODAL_ID,
} from '@heidi/commerce/data';
import { ModalId } from '@heidi/schema/ml-scribe';

/**
 * The modal tier register - source of truth for which tier each
 * queue-managed modal belongs to. The queue gate reads it via
 * `isModalDisplayAllowedByTier` in `modalTierFramework.ts`.
 *
 * T0/T1 inline surfaces (`GlobalModalsWrapper` `isOpen`) may register here — no queue.
 * See `modalTiers.handover.md`.
 *
 * To classify a modal: add `[ModalId.X]: Tier.Y` under the matching tier section
 * below, once product has confirmed placement. Duplicate keys are a compile
 * error, so a modal can't land in two tiers.
 *
 * **Partial during rollout** — an unlisted modal is not tier-governed at runtime
 * (the gate passes it through). But CI already blocks a NEW unlisted `ModalId`:
 * the set-diff gate in `modalTierFramework.test.ts` fails on any `ModalId` that is
 * neither listed here nor grandfathered in `untiered-modal-baseline.json` (a
 * shrink-only list, regenerated with `pnpm ratchet:untiered-modals:update`). End
 * state: every modal listed here, this becomes a total `Record<ModalId, Tier>`
 * (missing entry = compile error) and the baseline is deleted.
 */
export const MODAL_TIER_REGISTER: Partial<Record<ModalId, Tier>> = {
  // ── T0 · Product functionality — always shows; is the task ───────────────
  // Actual completed-copy output feedback, not recurring onboarding.
  [ModalId.ACTION_COPY_FORMATTING_LOSS_POPOVER]: Tier.ProductFunctionality,
  [ModalId.USER_SELF_ONBOARDING]: Tier.ProductFunctionality,
  [ModalId.T0_TEAM_MEMBER_REQUIRE_SPECIALTY_MODAL]: Tier.ProductFunctionality,
  // Action-triggered entry point of the device-setup flow at the connect
  // moment — task UI, not an education nudge. T4's enterprise lock would
  // hide setup from managed users who plug the mic in. Once-ever is showOnce.
  [ModalId.ACTION_DICTAPHONE_CONNECTED_DEVICE_SETUP_POPOVER]: Tier.ProductFunctionality,
  [ModalId.ACTION_DICTAPHONE_CONNECTED_DEVICE_SETUP_V2_POPOVER]:
    Tier.ProductFunctionality,
  // T0 not T3 as the doc's "team invite" example: T3 suppresses for enterprise,
  // who are the audience here — an invitee must be able to join regardless.
  [PENDING_TEAM_INVITE_MODAL_ID]: Tier.ProductFunctionality,
  // T0 inline, pending ModalId: LegacyOnboardingRequiredUserDetailsModal
  // ── T1 · Safety & system — must show, non-dismissible ────────────────────
  [ModalId.T1_NETWORK_FIREWALL_ERROR_MODAL]: Tier.SafetySystem,
  // ── T2 · Legal & compliance — must be acknowledged ───────────────────────
  [ModalId.CUSTOM_AUDIENCE_HEALTH_NZ_MIGRATION_MODAL]: Tier.LegalCompliance,
  [ModalId.CUSTOM_AUDIENCE_HCP_VERIFICATION_REMINDER_MODAL]: Tier.LegalCompliance,
  // ── T3 · Revenue & conversion — receptive moments only ───────────────────
  [ModalId.FREE_TRIAL]: Tier.RevenueConversion,
  [ModalId.T3_CUSTOM_AUDIENCE_CLINICIAN_MAX_CREDIT_USAGE_POPOVER]: Tier.RevenueConversion,
  // Auto-shown strip, not queued: it reaches members 23+ days in, so the
  // first-sessions grace does not apply.
  [COMPLIMENTARY_TRIAL_END_BANNER_MODAL_ID]: Tier.RevenueConversion,
  [COMPLIMENTARY_TRIAL_ENDED_MODAL_ID]: Tier.RevenueConversion,
  [CLINICIAN_TRIAL_ENDED_MODAL_ID]: Tier.RevenueConversion,
  // ── T4 · Onboarding & education — helpful nudge, 1/session ───────────────
  [ModalId.ACTION_COPY_SETTINGS_COACHMARK]: Tier.OnboardingEducation,
  [ModalId.SCRIBE_LAYOUT_V2_INVITE]: Tier.OnboardingEducation,
  [ModalId.CUSTOM_AUDIENCE_SCRIBE_LAYOUT_SWITCH_MODAL]: Tier.OnboardingEducation,
  [ModalId.CUSTOM_AUDIENCE_SCRIBE_LAYOUT_V2_TOUR_BANNER]: Tier.OnboardingEducation,
  [ModalId.T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT]: Tier.OnboardingEducation,
  [ModalId.T4_CUSTOM_AUDIENCE_ADAPTIVE_LEARNING_OPTIN_POPOVER]: Tier.OnboardingEducation,
  [ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_OPT_IN_BANNER]: Tier.OnboardingEducation,
  [ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_REQUEST_ACCESS_BANNER]: Tier.OnboardingEducation,
  // Schema leftover until BE drops this enum member (GRO-4483 rename to
  // T4_NEW_USER_HEIDI_2_ONBOARDING_BANNER). Remove on the next schema:dev.
  [ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_HOME_CHANGEBOARDING_BANNER]:
    Tier.OnboardingEducation,
  [HEIDI_2_NEW_USER_ONBOARDING_BANNER_MODAL_ID]: Tier.OnboardingEducation,
  [COMPLIMENTARY_USAGE_BANNER_MODAL_ID]: Tier.OnboardingEducation,
  [HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID]: Tier.OnboardingEducation,
  [HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID]: Tier.OnboardingEducation,
  [HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID]: Tier.OnboardingEducation,
  [HEIDI_2_CONFIRM_ACCESS_MODAL_ID]: Tier.OnboardingEducation,
  [DEFAULT_LANDING_PAGE_BANNER_MODAL_ID]: Tier.OnboardingEducation,
  // Library banner telling existing collection users that Library is now My
  // Sources. Inline on the Library page, not queued; the route applies the gate.
  [MY_SOURCES_CHANGEBOARDING_BANNER_MODAL_ID]: Tier.OnboardingEducation,
  // ACTION_DICTATED_GENERAL_REENGAGEMENT_POPOVER is unlisted so T4 R1 cannot
  // withhold an inline-armed card during a paused consult.
  // ── T5 · Product & marketing — only when settled ─────────────────────────
  [ModalId.T5_CUSTOM_AUDIENCE_SCRIBE_LAYOUT_CSAT_POPOVER]: Tier.ProductMarketing,
};
